import { Button } from '@/shared/components/ui';
import { cn } from '@/shared/lib/utils';
import {
  Employee,
  FormattedStepAnswer,
  getFeedbackReasonFlowAnswers,
  PopulatedFeedbackInteraction,
} from '@momentum/types';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from '@/shared/components/ui';
import { useMemo, useState } from 'react';
import { useEmployeeContext } from '@/shared/contexts/EmployeeContext';
import { useGoals } from '@/shared/hooks/useGoals';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@/shared/utils/date-utils';

interface ConversationDetailsProps {
  feedback: PopulatedFeedbackInteraction;
  feedbackCreator: Employee;
  isAlreadyFinished?: boolean;
  onAllYesClick?: () => void;
}

interface YesNoButtonsProps {
  onYesClick: () => void;
  disabled?: boolean;
}

const YesNoButtons: React.FC<YesNoButtonsProps> = ({
  onYesClick,
  disabled = false,
}) => {
  const { t } = useTranslation('openBlockage');

  return (
    <span className={cn('flex gap-2', disabled ? 'opacity-50' : '')}>
      <Button
        className="transition-colors duration-200"
        onClick={onYesClick}
        disabled={disabled}
      >
        {t('yes')}
      </Button>
      <Button
        variant="secondary"
        className="transition-colors duration-200"
        disabled={disabled}
      >
        {t('no')}
      </Button>
    </span>
  );
};

export function ConversationDetails({
  feedback,
  feedbackCreator,
  isAlreadyFinished = false,
  onAllYesClick,
}: ConversationDetailsProps) {
  const { t, i18n } = useTranslation('openBlockage');
  const { t: tWizard } = useTranslation('wizard');

  const me = useEmployeeContext();
  const { data: goals } = useGoals(me.companyId);
  // const { data: values } = useValues(me.companyId);

  const [isFirstSectionCompleted, setIsFirstSectionCompleted] =
    useState(isAlreadyFinished);
  const [isSecondSectionCompleted, setIsSecondSectionCompleted] =
    useState(isAlreadyFinished);
  const [isThirdSectionCompleted, setIsThirdSectionCompleted] =
    useState(isAlreadyFinished);
  const [isFourthSectionCompleted, setIsFourthSectionCompleted] =
    useState(isAlreadyFinished);
  const employee = useEmployeeContext();
  const feedbackCreatorName =
    feedbackCreator.personalInfo.firstName +
    ' ' +
    feedbackCreator.personalInfo.lastName;

  const formatAdditionalEffectsDetailed = (effects: string[]) => {
    const effectsOptions = tWizard(
      'feedbackQuestions.additionalEffects.options',
      {
        returnObjects: true,
      }
    ) as Record<
      string,
      { title: string; afterSend?: (name: string, impacted: string) => string }
    >;

    return effects
      .filter((effect) => effect !== 'no-other-effects')
      .map((effect) => {
        const effectOption = effectsOptions[effect];
        if (effectOption?.afterSend) {
          return effectOption.afterSend(feedbackCreatorName, '');
        }
        return effectOption?.title || effect;
      });
  };

  const reasonDetails: FormattedStepAnswer[] = useMemo(() => {
    const formatContext = {
      selectedEmployee: employee,
      formatDate: (date: Date | string) =>
        formatDate(date, 'dd MMM yyyy', i18n.language),
    };

    return getFeedbackReasonFlowAnswers(
      feedback.formData,
      tWizard as any,
      formatContext
    );
  }, [feedback.formData, tWizard, i18n.language, employee]);

  const handleFirstSectionYes = () => {
    setIsFirstSectionCompleted(true);
  };

  const handleSecondSectionYes = () => {
    setIsSecondSectionCompleted(true);
  };

  const handleThirdSectionYes = () => {
    setIsThirdSectionCompleted(true);
  };

  const handleFourthSectionYes = () => {
    setIsFourthSectionCompleted(true);
    onAllYesClick?.();
  };

  return (
    <div className="flex justify-center max-w-4xl mx-auto">
      <div>
        <Card className="flex flex-col justify-centers shadow-2xl bg-gradient-to-br">
          <CardHeader>
            <CardTitle className="text-2xl font-semibold">
              {t('hi')},{' '}
              <span className="text-primary">
                {employee.personalInfo.firstName +
                  ' ' +
                  employee.personalInfo.lastName}
              </span>
            </CardTitle>
            <span>{t('youveReceivedRequestFrom')(feedbackCreatorName)}</span>
            <CardDescription>{t('readDetailsInvitation')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Card className="shadow-lg">
              <CardHeader className="font-semibold">
                {t('detailsOfEvent')}
              </CardHeader>
              <CardContent>
                <ul className="list-disc list-inside space-y-4">
                  {feedback.formData['date-selection'] && (
                    <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                      <p className="text-sm font-medium text-gray-600">
                        {tWizard('feedbackQuestions.review.date')}
                      </p>
                      <p className="text-base font-semibold text-gray-900 mt-1">
                        {formatDate(
                          feedback.formData['date-selection'] as Date,
                          'dd MMM yyyy',
                          i18n.language
                        )}
                      </p>
                    </div>
                  )}

                  {reasonDetails.length > 0 && (
                    <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                      <p className="text-sm font-medium text-gray-600 mb-6">
                        {tWizard('feedbackQuestions.review.reasonDetails')}
                      </p>

                      <div className="space-y-4">
                        {reasonDetails.length > 0 && (
                          <div
                            key={`${reasonDetails[0]?.stepId}-${0}`}
                            className="space-y-1"
                          >
                            <p className="text-sm font-medium text-gray-500">
                              {reasonDetails[0]?.formattedQuestion}
                            </p>
                            <p className="text-base text-gray-900">
                              {reasonDetails[1]?.formattedAnswer}
                            </p>
                          </div>
                        )}
                        {reasonDetails.slice(2).map((item, index) => (
                          <div
                            key={`${item.stepId}-${index}`}
                            className="space-y-1"
                          >
                            <p className="text-sm font-medium text-gray-500">
                              {item.formattedQuestion}
                            </p>
                            <p className="text-base text-gray-900">
                              {item.formattedAnswer}
                            </p>
                          </div>
                        ))}
                        {feedback.formData['interaction-description'] && (
                          <div
                            key={`${reasonDetails[0]?.stepId}-${0}`}
                            className="space-y-1"
                          >
                            <p className="text-sm font-medium text-gray-500">
                              {tWizard('common.labels.interactionDescription')}
                            </p>
                            <p className="text-base text-gray-900">
                              {feedback.formData['interaction-description']}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </ul>
              </CardContent>
              <CardFooter className="flex justify-between">
                {t('areEventDetailsClear')}
                <YesNoButtons
                  onYesClick={handleFirstSectionYes}
                  disabled={isFirstSectionCompleted}
                />
              </CardFooter>
            </Card>
            <Card
              className={`shadow-lg transition-opacity duration-300 ${
                !isFirstSectionCompleted ? 'opacity-50' : ''
              }`}
            >
              <CardHeader className="font-semibold">
                {t('impactOnGoals')(feedbackCreatorName)}
              </CardHeader>
              <CardContent>
                {'goal-selection' in feedback.formData &&
                  feedback.formData['goal-selection'] && (
                    <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                      <p className="text-base text-gray-900 mt-1">
                        {tWizard(
                          'feedbackQuestions.review.goal.prefixAfterSend',
                          { employeeName: feedbackCreatorName }
                        )}{' '}
                        <span className="font-semibold">
                          {
                            goals?.find(
                              (goal) =>
                                goal._id === feedback.formData['goal-selection']
                            )?.title
                          }
                        </span>{' '}
                        {tWizard('feedbackQuestions.review.goal.affection')}{' '}
                        <span className="font-semibold">
                          {feedback.formData['goal-impact-range'] === 1
                            ? tWizard('common.rangeDefaults.relatively')
                            : feedback.formData['goal-impact-range'] === 2
                            ? tWizard('common.rangeDefaults.significantly')
                            : tWizard('common.rangeDefaults.extremely')}
                        </span>
                      </p>
                    </div>
                  )}
              </CardContent>
              <CardFooter className="flex justify-between">
                {t('isItClearHowGoalsAffected')(feedbackCreatorName)}
                <YesNoButtons
                  onYesClick={handleSecondSectionYes}
                  disabled={
                    !isFirstSectionCompleted || isSecondSectionCompleted
                  }
                />
              </CardFooter>
            </Card>

            <Card>
              <CardHeader className="font-semibold">
                {t('furtherImpact')}
              </CardHeader>
              <CardContent>
                {'additional-effects' in feedback.formData &&
                  feedback.formData['additional-effects'] && (
                    <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                      <ul className="space-y-2">
                        {formatAdditionalEffectsDetailed(
                          feedback.formData['additional-effects']
                        ).map((effectText, index) => (
                          <li
                            key={index}
                            className="text-base list-disc list-inside"
                          >
                            {effectText}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
              </CardContent>
              <CardFooter className="flex justify-between">
                {t('isItClearHowAffected')(feedbackCreatorName)}
                <YesNoButtons
                  onYesClick={handleThirdSectionYes}
                  disabled={
                    !isSecondSectionCompleted || isThirdSectionCompleted
                  }
                />
              </CardFooter>
            </Card>
            <Card
              className={`transition-opacity duration-300 ${
                !isSecondSectionCompleted ? 'opacity-50' : ''
              }`}
            >
              <CardHeader className="flex flex-row justify-between">
                <span className="max-w-3/4">
                  {t('nowThatRequestIsClear')(feedbackCreatorName)}
                </span>
                <YesNoButtons
                  onYesClick={handleFourthSectionYes}
                  disabled={
                    !isThirdSectionCompleted || isFourthSectionCompleted
                  }
                />
              </CardHeader>
            </Card>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
